import LiveApiClient from "../api/LiveApiClient";
import OfflineStore from "./OfflineStore";
import PrecacheIndex from "./PrecacheIndex";
import PageItemsCache from "./PageItemsCache";
import OfflineErrors from "./OfflineErrors";
import { checkCacheVersion, setCacheVersion, invalidatePrecachePrimaryCache } from "./PrecacheConfig";
import { REMOTE_BASE_URL } from "../constants";

const IMAGE_CACHE_NAME = "offline-images-v1";
const ASSET_CACHE_NAME = "offline-assets-v1";
// Use the centralized CDN constant instead of hardcoding
const ASSET_CDN_URL = REMOTE_BASE_URL;

/**
 * OfflineSync — handles full catalog download for offline/cache-primary mode.
 *
 * Features:
 *   - Uses catalog-level items endpoint (items synced once, no page duplicates)
 *   - Priority first-page fetch (display order) for instant UI
 *   - Cursor-based pagination for consistent results
 *   - Delta sync with deleted item handling
 *   - Stores items with filter values for client-side filtering
 *   - Caches product images for offline viewing
 *   - Caches product page assets (images, videos, PDFs) for offline viewing
 *   - Tracks sync time for delta updates
 *   - Provides progress callbacks for UI feedback
 *
 * Endpoints:
 *   GET /Catalog/{catalogId}/items/meta - Returns pages, count, and sync_time
 *   GET /Catalog/{catalogId}/items - Returns items with cursor pagination (limit=500)
 *   GET /Catalog/{catalogId}/items?page_id={pageId} - Returns items for a specific page
 *   GET /Catalog/{catalogId}/items/assets - Returns product page assets (images, videos, PDFs)
 *   GET /Catalog/{catalogId}/fields - Returns field definitions (thumb/detail display config)
 * Filters are fetched separately per page: GET /Catalog_Page/{pageId}/filters
 *
 * Usage:
 *   const sync = new OfflineSync(catalogContext, apiKeyContext);
 *   await sync.start({
 *     onProgress: ({ downloaded, total, percent, phase }) => updateUI(percent, phase),
 *     onComplete: ({ itemCount, syncTime, imagesCached, assetsCached }) => showSuccess(),
 *     onError: (error) => showError(error),
 *     onFirstPage: ({ items, total }) => showFirstPage(items),
 *   });
 */
export default class OfflineSync {
	#catalogContext;
	#apiKeyContext;
	#abortController = null;
	#PAGE_SIZE = 500;
	#IMAGE_BATCH_SIZE = 10; // Concurrent image downloads
	#newItemIdsThisSync = []; // Track item IDs (not full objects) synced for targeted image caching
	#deletedItemIdsThisSync = []; // Track deleted item IDs for PrecacheIndex delta updates
	#newItemsThisSync = []; // Track actual items for delta sync index update (avoids re-reading from IDB)

	constructor(catalogContext, apiKeyContext) {
		this.#catalogContext = catalogContext;
		this.#apiKeyContext = apiKeyContext;
	}

	/**
	 * Start the sync process.
	 * @param {Object} options
	 * @param {Function} options.onProgress - Called with { downloaded, total, percent }
	 * @param {Function} options.onComplete - Called with { itemCount, syncTime, imagesCached }
	 * @param {Function} options.onError - Called with error
	 * @param {Function} options.onFirstPage - Called when first page is ready (for instant UI)
	 * @param {boolean} options.deltaOnly - Only sync changes since last sync
	 * @param {boolean} options.resume - Resume from saved progress
	 * @param {boolean} options.cacheImages - Whether to cache product images (default: true)
	 * @param {number|null} options.priorityPageId - Catalog page ID to sync first (for page-by-page loading)
	 */
	async start({ onProgress, onComplete, onError, onFirstPage, deltaOnly = false, resume = false, cacheImages = true, priorityPageId = null } = {}) {
		// Abort any previous sync before starting a new one
		if (this.#abortController && !this.#abortController.signal.aborted) {
			this.#abortController.abort();
		}
		this.#abortController = new AbortController();
		this.#newItemIdsThisSync = []; // Reset for this sync session
		this.#deletedItemIdsThisSync = []; // Reset for this sync session
		this.#newItemsThisSync = []; // Reset for this sync session
		const syncStartTime = new Date().toISOString();

		try {
			// Get a catalog-bound store instance (no global state mutation)
			const store = OfflineStore.ForCatalog(this.#catalogContext.id);

			// Bail out if IndexedDB is unavailable (circuit breaker tripped)
			if (!store.isAvailable) {
				console.warn("[OfflineSync] IndexedDB unavailable, skipping sync");
				onError?.(new Error("IndexedDB unavailable"));
				return;
			}

			// Check cache version - if outdated, clear and force full sync
			const { needsRebuild, currentVersion, cachedVersion } = await checkCacheVersion(this.#catalogContext.id);
			if (needsRebuild) {
				console.warn(`[OfflineSync] Cache version mismatch (cached: ${cachedVersion}, current: ${currentVersion}). Clearing cache...`);
				await store.ClearPrecacheData();
				await store.ClearSyncProgress();
				// Force full sync (not delta, not resume)
				deltaOnly = false;
				resume = false;
			}

			// Check for existing sync time for delta sync
			const lastSyncTime = deltaOnly
				? await store.GetSyncMetadata("lastSyncTime")
				: null;

			let total = 0;
			let downloaded = 0;
			let syncTime = null;
			let hasMore = true;
			// Cursor-based pagination (keyset pagination)
			let afterId = null;

			// Check for saved progress to resume
			if (resume) {
				const savedProgress = await store.GetSyncProgress();
				if (savedProgress) {
					total = savedProgress.total || 0;
					downloaded = savedProgress.downloaded || 0;

					if (savedProgress.phase === 'data') {
						// Resume data fetching from cursor
						afterId = savedProgress.afterId ?? null;

						// Report initial progress
						onProgress?.({
							downloaded,
							total,
							percent: total > 0 ? Math.round((downloaded / total) * 100) : 0,
							phase: "data",
							resumed: true,
						});
					} else if (['indexing', 'images', 'assets'].includes(savedProgress.phase)) {
						// Data is already complete, skip to the saved phase
						// The image/asset caching logic already handles checking what's cached
						hasMore = false; // Skip data fetching loop

						console.warn(`[OfflineSync] Resuming from ${savedProgress.phase} phase`);
						onProgress?.({
							downloaded,
							total,
							percent: 100,
							phase: savedProgress.phase,
							resumed: true,
						});
					}
				}
			}

			// Initialize streaming index build for full syncs.
			// Instead of loading all items 3 times in post-processing, we build
			// the index incrementally as items arrive in the download loop.
			const precacheIndex = PrecacheIndex.ForCatalog(this.#catalogContext.id);
			const allTerms = new Set();
			const allImageUrls = new Set();

			if (!deltaOnly && !resume) {
				precacheIndex.beginIncrementalBuild();
			}

			// Priority first-page fetch: get first 60 items in display order
			// This allows the UI to show content immediately while sync continues
			// If priorityPageId is provided, fetch items for that specific page first
			if (!resume && !deltaOnly) {
				const firstPageResult = await this.#fetchPriorityFirstPage(priorityPageId);
				if (firstPageResult) {
					total = firstPageResult.total;
					syncTime = firstPageResult.sync_time;

					// Cache field definitions from meta response (new API shape)
					if (firstPageResult.fields) {
						await store.CacheFieldDefs(firstPageResult.fields);
					}

					// Cache on_demand configuration for price/inventory fetchers
					if (firstPageResult.on_demand) {
						await store.SetSyncMetadata("on_demand", firstPageResult.on_demand);
					}

					// Cache meta filters and build referenceMap immediately.
					// The referenceMap maps filter references (e.g. "material-flat") to
					// common filter IDs (e.g. "3") which are the keys in item.filters.
					// Building it here (during sync) ensures it's correct before any
					// filter request reads it — CacheFilters runs later (on /filters
					// response) and may miss the window.
					if (firstPageResult.meta_filters) {
						await store.SetSyncMetadata("meta_filters", firstPageResult.meta_filters);
						await store.BuildReferenceMapFromMeta(firstPageResult.meta_filters);
					}

					// Store first page items
					if (firstPageResult.items?.length > 0) {
						await store.CachePrecacheItems(firstPageResult.items);
						downloaded = firstPageResult.items.length;
						this.#newItemIdsThisSync.push(...firstPageResult.items.map(i => i.id));

						// Streaming index build: ingest batch + collect terms + image URLs
						const { terms } = precacheIndex.ingestBatch(firstPageResult.items);
						for (const t of terms) allTerms.add(t);
						for (const url of this.#extractImageUrls(firstPageResult.items)) allImageUrls.add(url);

						// Notify UI that first page is ready for display
						onFirstPage?.({
							items: firstPageResult.items,
							total,
							pageId: priorityPageId,
						});
					}

					// Continue from where priority fetch left off
					afterId = firstPageResult.next_cursor?.after_id;
					hasMore = firstPageResult.has_more;

					onProgress?.({
						downloaded,
						total,
						percent: total > 0 ? Math.round((downloaded / total) * 100) : 0,
						phase: "data",
					});
				}
			}

			// Cursor-based sync: fetch all items using cursor pagination
			while (hasMore) {
					if (this.#abortController.signal.aborted) {
						throw new Error("Sync cancelled");
					}

					// First page needs meta (for resume/delta sync where we skip priority first page)
					const needsMeta = downloaded === 0 || (resume && total === 0);
					const result = await this.#fetchPage({ afterId, since: lastSyncTime, includeMeta: needsMeta });

					// First page includes pages and total
					if (needsMeta) {
						total = result.total;
						syncTime = result.sync_time;

						// Cache field definitions from meta response (new API shape)
						if (result.fields) {
							await store.CacheFieldDefs(result.fields);
						}

						// Cache on_demand configuration for price/inventory fetchers
						if (result.on_demand) {
							await store.SetSyncMetadata("on_demand", result.on_demand);
						}

						// Cache meta filters and build referenceMap
						if (result.meta_filters) {
							await store.SetSyncMetadata("meta_filters", result.meta_filters);
							await store.BuildReferenceMapFromMeta(result.meta_filters);
						}
					}

					// Store items and track for image caching
					if (result.items?.length > 0) {
						await store.CachePrecacheItems(result.items);
						downloaded += result.items.length;
						// Track new items for targeted image caching
						this.#newItemIdsThisSync.push(...result.items.map(i => i.id));

						if (deltaOnly) {
							// Delta sync: keep actual items for direct index update (avoids re-reading from IDB)
							this.#newItemsThisSync.push(...result.items);
						} else if (!resume) {
							// Full sync: streaming index build + collect terms + image URLs
							const { terms } = precacheIndex.ingestBatch(result.items);
							for (const t of terms) allTerms.add(t);
							for (const url of this.#extractImageUrls(result.items)) allImageUrls.add(url);
						}
					}

					// Handle deleted items (delta sync only)
					if (result.deleted?.length > 0) {
						await store.RemovePrecacheItems(result.deleted);
						this.#deletedItemIdsThisSync.push(...result.deleted);
						console.warn(`[OfflineSync] Removed ${result.deleted.length} deleted items`);
					}

					hasMore = result.has_more;

					// Update cursor from response for next page
					if (result.next_cursor) {
						afterId = result.next_cursor.after_id;
					}

					// Safety check: stop if no cursor returned (shouldn't happen with has_more=true)
					if (hasMore && !result.next_cursor) {
						console.warn("[OfflineSync] has_more=true but no next_cursor, stopping pagination");
						hasMore = false;
					}

					// Save progress for resume capability (using cursor instead of offset)
					await store.SaveSyncProgress({
						afterId,
						total,
						downloaded,
						phase: hasMore ? 'data' : 'indexing',
					});

					// Progress callback
					onProgress?.({
						downloaded,
						total,
						percent: total > 0 ? Math.round((downloaded / total) * 100) : 0,
						phase: "data",
					});
				}

			// Save sync time for future delta syncs
			// Use API-provided syncTime, or fall back to when this sync started
			const finalSyncTime = syncTime || syncStartTime;
			await store.SetSyncMetadata("lastSyncTime", finalSyncTime);

			// Finalize indexing: for full syncs the index was built incrementally
			// during the download loop. For delta syncs, update the existing index.
			await store.SaveSyncProgress({ total, downloaded, phase: 'indexing' });
			onProgress?.({
				downloaded,
				total,
				percent: 100,
				phase: "indexing",
			});

			if (!deltaOnly && !resume) {
				// Full sync: finalize the incrementally-built index
				// pageIndex is populated on-demand by PageItemsCache, not during sync
				await precacheIndex.finalizeIncrementalBuild();
				await store.CacheTermDictionary(allTerms);
			} else {
				// Delta sync or resume: use the old post-processing paths
				await this.#buildTermDictionary(store, deltaOnly);
				await this.#buildPrecacheIndex(store, deltaOnly);
			}

			// Phase 2: Cache images (only if enabled and for new items, skip already-cached images)
			let imagesCached = 0;

			if (cacheImages) {
				const previousImageSync = await store.GetSyncMetadata("imageSyncComplete");

				if (!previousImageSync) {
					// First sync - use pre-collected URLs (avoids loading all items from IDB)
					await store.SaveSyncProgress({ total, downloaded, phase: 'images' });
					onProgress?.({
						downloaded,
						total,
						percent: 100,
						phase: "images",
						imagesCached: 0,
						imagesTotal: 0,
					});

					if (allImageUrls.size > 0) {
						imagesCached = await this.#cacheCollectedImages(allImageUrls, onProgress);
					} else {
						// Fallback for resume — URLs weren't collected during download
						imagesCached = await this.#cacheImages(store, onProgress);
					}
					await store.SetSyncMetadata("imageSyncComplete", true);
				} else if (this.#newItemIdsThisSync.length > 0) {
					// Subsequent sync with new items - only cache images from new items
					await store.SaveSyncProgress({ total, downloaded, phase: 'images' });
					onProgress?.({
						downloaded,
						total,
						percent: 100,
						phase: "images",
						imagesCached: 0,
						imagesTotal: 0,
					});

					imagesCached = await this.#cacheNewItemImages(store, onProgress);
				} else {
					// Images already cached and no new items - skip entirely
					onProgress?.({
						downloaded,
						total,
						percent: 100,
						phase: "images",
						skipped: true,
					});
				}
			}

			// Phase 3: Cache assets (product page images, videos, PDFs)
			let assetsCached = 0;

			await store.SaveSyncProgress({ total, downloaded, phase: 'assets' });
			onProgress?.({
				downloaded,
				total,
				percent: 100,
				phase: "assets",
				assetsCached: 0,
				assetsTotal: 0,
			});

			assetsCached = await this.#cacheAssets(onProgress);

			// Mark sync as complete and clear progress
			await store.SaveSyncProgress({ total, downloaded, phase: 'complete' });
			await store.ClearSyncProgress();

			// Store cache version after successful sync
			await setCacheVersion(this.#catalogContext.id);

			// Invalidate caches so next page visit fetches fresh data
			PageItemsCache.invalidateAll();
			invalidatePrecachePrimaryCache(this.#catalogContext.id);

			// Pre-warm page items cache for all pages (fire and forget)
			const pages = this.#catalogContext.pages;
			if (Array.isArray(pages)) {
				const catalogId = this.#catalogContext.id;
				const accountCode = this.#catalogContext.account?.code;
				const apiKey = this.#apiKeyContext?.apiKey;
				for (const p of pages) {
					if (p.type?.code === 'item-gallery' && p.id) {
						PageItemsCache.getPageItems(p.id, catalogId, accountCode, apiKey);
					}
				}
			}

			onComplete?.({ itemCount: downloaded, syncTime, imagesCached, assetsCached });

			// Report sync success to Sentry as breadcrumb
			if (typeof window !== "undefined" && window.Sentry) {
				window.Sentry.addBreadcrumb({
					category: "offline",
					message: "Sync complete",
					level: "info",
					data: { catalogId: this.#catalogContext?.id, itemCount: downloaded, imagesCached, assetsCached },
				});
			}

		} catch (error) {
			if (error.name === "AbortError" || error.message === "Sync cancelled") {
				// Sync cancelled by user
			} else {
				console.error("[OfflineSync] Sync failed:", error);
				OfflineErrors.log("sync_failed", error, { catalogId: this.#catalogContext?.id });
				onError?.(error);
			}
		}
	}

	/**
	 * Cancel an in-progress sync.
	 */
	cancel() {
		this.#abortController?.abort();
	}

	/**
	 * Build a dictionary of unique terms from cached items for autocorrect.
	 * Extracts words from name, keywords, and other searchable fields.
	 */
	async #buildTermDictionary(store, deltaOnly = false) {
		try {
			const minTermLength = 3;

			if (deltaOnly) {
				// Delta sync: merge new terms into existing dictionary
				// (avoids loading all items from IDB)
				if (this.#newItemsThisSync.length === 0) return;

				const existingTerms = await store.GetTermDictionary();
				const terms = new Set(existingTerms || []);

				for (const item of this.#newItemsThisSync) {
					this.#extractTermsFromItem(item, terms, minTermLength);
				}

				await store.CacheTermDictionary(terms);
			} else {
				// Resume: full rebuild from all items in store
				const { items } = await store.GetPrecachedItems();
				if (items.length === 0) return;

				const terms = new Set();
				for (const item of items) {
					this.#extractTermsFromItem(item, terms, minTermLength);
				}

				await store.CacheTermDictionary(terms);
			}
		} catch (error) {
			console.error("[OfflineSync] Failed to build term dictionary:", error);
			OfflineErrors.log("buildTermDictionary", error);
			// Non-fatal - autocorrect just won't work
		}
	}

	/**
	 * Extract searchable terms from a single item into a Set.
	 */
	#extractTermsFromItem(item, terms, minTermLength) {
		const attribs = item.attribs || {};

		const searchableText = [
			attribs.name,
			attribs.title,
			attribs.description,
			attribs.handle,
			attribs.keywords,
			attribs.category,
			attribs.family,
			attribs.style,
			attribs.color_swatch,
		].filter(Boolean).join(" ");

		const words = searchableText
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, " ")
			.split(/\s+/)
			.filter(word => word.length >= minTermLength);

		for (const word of words) terms.add(word);
	}

	/**
	 * Build or update the PrecacheIndex for fast offline filtering/search/pagination.
	 * Full syncs do a complete rebuild; delta syncs do an incremental update.
	 * @param {OfflineStore} store - Catalog-bound OfflineStore instance
	 * @param {boolean} deltaOnly - Whether this is a delta sync
	 */
	async #buildPrecacheIndex(store, deltaOnly) {
		try {
			const catalogId = this.#catalogContext.id;
			const precacheIndex = PrecacheIndex.ForCatalog(catalogId);

			if (deltaOnly && this.#newItemIdsThisSync.length === 0 && this.#deletedItemIdsThisSync.length === 0) {
				// Delta sync with no changes — nothing to update
				return;
			}

			if (deltaOnly) {
				// Incremental update: use items collected during download (avoids re-reading from IDB)
				await precacheIndex.updateFromDelta(this.#newItemsThisSync, this.#deletedItemIdsThisSync);
				console.warn(`[OfflineSync] PrecacheIndex delta updated: ${this.#newItemsThisSync.length} added/changed, ${this.#deletedItemIdsThisSync.length} deleted`);
			} else {
				// Full rebuild from all items in store
				const { items: allItems } = await store.GetPrecachedItems();
				await precacheIndex.buildFromItems(allItems);
				console.warn(`[OfflineSync] PrecacheIndex built from ${allItems.length} items`);
			}
		} catch (error) {
			console.error("[OfflineSync] Failed to build PrecacheIndex:", error);
			OfflineErrors.log("buildPrecacheIndex", error);
			// Non-fatal - falls back to brute-force filtering in OfflineStore
		}
	}

	/**
	 * Cache images from precached items.
	 * @param {Function} onProgress - Progress callback
	 * @returns {Promise<number>} Number of images cached
	 */
	async #cacheImages(store, onProgress) {
		try {
			// Check storage quota before starting image cache
			if (navigator.storage?.estimate) {
				const { usage, quota } = await navigator.storage.estimate();
				if (quota && (usage / quota) > 0.9) {
					console.warn("[OfflineSync] Storage > 90% full, skipping image cache");
					return 0;
				}
			}

			const { items } = await store.GetPrecachedItems();
			if (items.length === 0) return 0;

			// Extract unique image URLs from items
			const imageUrls = this.#extractImageUrls(items);

			if (imageUrls.length === 0) return 0;

			// Open or create the image cache
			const cache = await caches.open(IMAGE_CACHE_NAME);

			// Check which images are already cached (batched for performance)
			const cacheChecks = await Promise.all(
				imageUrls.map(url => cache.match(url).then(r => ({ url, cached: !!r })))
			);
			const uncachedUrls = cacheChecks.filter(c => !c.cached).map(c => c.url);

			if (uncachedUrls.length === 0) return imageUrls.length;

			let cached = imageUrls.length - uncachedUrls.length;
			const total = imageUrls.length;

			// Cache images in batches to avoid overwhelming the browser
			for (let i = 0;i < uncachedUrls.length;i += this.#IMAGE_BATCH_SIZE) {
				if (this.#abortController.signal.aborted) {
					throw new Error("Sync cancelled");
				}

				// Stop caching if storage is nearly full
				if (navigator.storage?.estimate && i > 0 && i % (this.#IMAGE_BATCH_SIZE * 10) === 0) {
					const { usage, quota } = await navigator.storage.estimate();
					if (quota && (usage / quota) > 0.95) {
						console.warn("[OfflineSync] Storage > 95% full, stopping image cache");
						break;
					}
				}

				const batch = uncachedUrls.slice(i, i + this.#IMAGE_BATCH_SIZE);
				const results = await Promise.allSettled(
					batch.map(url => this.#cacheImage(cache, url))
				);

				// Count successful caches
				const successful = results.filter(r => r.status === "fulfilled" && r.value).length;
				cached += successful;

				onProgress?.({
					phase: "images",
					imagesCached: cached,
					imagesTotal: total,
					percent: Math.round((cached / total) * 100),
				});
			}

			return cached;

		} catch (error) {
			console.error("[OfflineSync] Image caching failed:", error);
			OfflineErrors.log("cacheImages", error);
			// Don't fail the whole sync for image caching errors
			return 0;
		}
	}

	/**
	 * Cache images from a pre-collected Set of URLs.
	 * Used by the streaming build path to avoid re-loading all items from IDB.
	 * @param {Set<string>} imageUrlSet - Pre-collected image URLs
	 * @param {Function} onProgress - Progress callback
	 * @returns {Promise<number>} Number of images cached
	 */
	async #cacheCollectedImages(imageUrlSet, onProgress) {
		try {
			// Check storage quota before starting image cache
			if (navigator.storage?.estimate) {
				const { usage, quota } = await navigator.storage.estimate();
				if (quota && (usage / quota) > 0.9) {
					console.warn("[OfflineSync] Storage > 90% full, skipping image cache");
					return 0;
				}
			}

			const imageUrls = Array.from(imageUrlSet);
			if (imageUrls.length === 0) return 0;

			const cache = await caches.open(IMAGE_CACHE_NAME);

			// Check which images are already cached (batched for performance)
			const cacheChecks = await Promise.all(
				imageUrls.map(url => cache.match(url).then(r => ({ url, cached: !!r })))
			);
			const uncachedUrls = cacheChecks.filter(c => !c.cached).map(c => c.url);

			if (uncachedUrls.length === 0) return imageUrls.length;

			let cached = imageUrls.length - uncachedUrls.length;
			const total = imageUrls.length;

			// Cache images in batches to avoid overwhelming the browser
			for (let i = 0; i < uncachedUrls.length; i += this.#IMAGE_BATCH_SIZE) {
				if (this.#abortController.signal.aborted) {
					throw new Error("Sync cancelled");
				}

				// Stop caching if storage is nearly full
				if (navigator.storage?.estimate && i > 0 && i % (this.#IMAGE_BATCH_SIZE * 10) === 0) {
					const { usage, quota } = await navigator.storage.estimate();
					if (quota && (usage / quota) > 0.95) {
						console.warn("[OfflineSync] Storage > 95% full, stopping image cache");
						break;
					}
				}

				const batch = uncachedUrls.slice(i, i + this.#IMAGE_BATCH_SIZE);
				const results = await Promise.allSettled(
					batch.map(url => this.#cacheImage(cache, url))
				);

				const successful = results.filter(r => r.status === "fulfilled" && r.value).length;
				cached += successful;

				onProgress?.({
					phase: "images",
					imagesCached: cached,
					imagesTotal: total,
					percent: Math.round((cached / total) * 100),
				});
			}

			return cached;

		} catch (error) {
			console.error("[OfflineSync] Collected image caching failed:", error);
			OfflineErrors.log("cacheCollectedImages", error);
			return 0;
		}
	}

	/**
	 * Cache images only from newly synced items (not all cached items).
	 * This is more efficient for delta syncs where most images are already cached.
	 * @param {Function} onProgress - Progress callback
	 * @returns {Promise<number>} Number of images cached
	 */
	async #cacheNewItemImages(store, onProgress) {
		try {
			if (this.#newItemIdsThisSync.length === 0) return 0;

			// Fetch only the new items from IndexedDB (avoids holding all items in memory)
			const newItems = await store.GetPrecachedItemsByIds(this.#newItemIdsThisSync);

			// Extract unique image URLs only from new items
			const imageUrls = this.#extractImageUrls(newItems);

			if (imageUrls.length === 0) return 0;

			// Open or create the image cache
			const cache = await caches.open(IMAGE_CACHE_NAME);

			// Check which images are already cached (batched for performance)
			const cacheChecks = await Promise.all(
				imageUrls.map(url => cache.match(url).then(r => ({ url, cached: !!r })))
			);
			const uncachedUrls = cacheChecks.filter(c => !c.cached).map(c => c.url);

			if (uncachedUrls.length === 0) return imageUrls.length;

			let cached = imageUrls.length - uncachedUrls.length;
			const total = imageUrls.length;

			// Cache images in batches
			for (let i = 0;i < uncachedUrls.length;i += this.#IMAGE_BATCH_SIZE) {
				if (this.#abortController.signal.aborted) {
					throw new Error("Sync cancelled");
				}

				const batch = uncachedUrls.slice(i, i + this.#IMAGE_BATCH_SIZE);
				const results = await Promise.allSettled(
					batch.map(url => this.#cacheImage(cache, url))
				);

				const successful = results.filter(r => r.status === "fulfilled" && r.value).length;
				cached += successful;

				onProgress?.({
					phase: "images",
					imagesCached: cached,
					imagesTotal: total,
					percent: Math.round((cached / total) * 100),
				});
			}

			return cached;

		} catch (error) {
			console.error("[OfflineSync] New item image caching failed:", error);
			OfflineErrors.log("cacheNewItemImages", error);
			return 0;
		}
	}

	/**
	 * Cache assets (images, videos, PDFs) from the items/assets endpoint.
	 * These are product page assets like banners, spec sheets, and demo videos.
	 * Stores both the asset files (in Cache API) and metadata (in IndexedDB).
	 * @param {Function} onProgress - Progress callback
	 * @returns {Promise<number>} Number of assets cached
	 */
	async #cacheAssets(onProgress) {
		try {
			// Fetch assets from the API
			const { assets } = await this.#fetchAssets();

			if (!assets || assets.length === 0) return 0;

			// Store asset metadata in IndexedDB for offline gallery display
			const store = OfflineStore.ForCatalog(this.#catalogContext.id);
			await store.CacheAssets(assets);

			// Build full URLs from asset sources
			const assetUrls = assets
				.map(asset => {
					if (!asset.source) return null;
					// Prepend CDN URL to the path
					return asset.source.startsWith("http")
						? asset.source
						: ASSET_CDN_URL + asset.source;
				})
				.filter(Boolean);

			if (assetUrls.length === 0) return 0;

			// Open or create the asset cache
			const cache = await caches.open(ASSET_CACHE_NAME);

			// Check which assets are already cached
			const uncachedUrls = [];
			for (const url of assetUrls) {
				const cached = await cache.match(url);
				if (!cached) {
					uncachedUrls.push(url);
				}
			}

			if (uncachedUrls.length === 0) return assetUrls.length;

			let cached = assetUrls.length - uncachedUrls.length;
			const total = assetUrls.length;

			// Cache assets in batches
			for (let i = 0; i < uncachedUrls.length; i += this.#IMAGE_BATCH_SIZE) {
				if (this.#abortController.signal.aborted) {
					throw new Error("Sync cancelled");
				}

				const batch = uncachedUrls.slice(i, i + this.#IMAGE_BATCH_SIZE);
				const results = await Promise.allSettled(
					batch.map(url => this.#cacheAsset(cache, url))
				);

				const successful = results.filter(r => r.status === "fulfilled" && r.value).length;
				cached += successful;

				onProgress?.({
					phase: "assets",
					assetsCached: cached,
					assetsTotal: total,
					percent: Math.round((cached / total) * 100),
				});
			}

			return cached;

		} catch (error) {
			console.error("[OfflineSync] Asset caching failed:", error);
			OfflineErrors.log("cacheAssets", error);
			// Don't fail the whole sync for asset caching errors
			return 0;
		}
	}

	/**
	 * Cache a single asset (image, video, or PDF).
	 * @param {Cache} cache - Cache object
	 * @param {string} url - Asset URL
	 * @returns {Promise<boolean>} Success
	 */
	async #cacheAsset(cache, url) {
		try {
			const response = await fetch(url, {
				mode: "no-cors",
				signal: this.#abortController.signal,
			});

			if (response.type === "opaque" || response.ok) {
				await cache.put(url, response);
				return true;
			}
			return false;
		} catch (error) {
			OfflineErrors.log("cacheAsset", error, { url });
			return false;
		}
	}

	/**
	 * Extract unique image URLs from items, including size variations.
	 * Components use processImagePath(source, size) which adds suffixes like .f.jpg (thumb)
	 * or .m.jpg (mini), so we need to cache those variations, not just the base URL.
	 * @param {Array} items - Precached items
	 * @returns {string[]} Array of image URLs
	 */
	#extractImageUrls(items) {
		const urls = new Set();
		// Cache only the thumb/full size (.f.) - good balance of quality and size
		// This covers most use cases (gallery, cards) without tripling storage
		const sizeVariation = "f";

		for (const item of items) {
			const attribs = item.attribs || item;

			// Get primary image (image_0 is the thumbnail/main image)
			if (attribs.image_0) {
				this.#addSizedImageUrl(attribs.image_0, urls, sizeVariation);
			}

			// Also check for media array with images
			const media = attribs.media || item.media;
			if (Array.isArray(media)) {
				for (const m of media) {
					if (m.type === "image" && m.source) {
						this.#addSizedImageUrl(m.source, urls, sizeVariation);
					}
				}
			}
		}

		return Array.from(urls);
	}

	/**
	 * Add a sized image URL to the set.
	 * Mirrors the logic from processImagePath in Functions.js
	 * @param {string} path - Image path
	 * @param {Set} urls - Set to add URLs to
	 * @param {string} size - Size suffix (e.g., "f" for thumb/full)
	 */
	#addSizedImageUrl(path, urls, size) {
		const baseUrl = this.#normalizeImageUrl(path);
		if (!baseUrl) return;

		// Check if it's SVG or WebP - these don't have size variations
		const ext = baseUrl.split(".").pop()?.toLowerCase();
		if (ext === "svg" || ext === "webp") {
			urls.add(baseUrl);
			return;
		}

		// Add sized URL (e.g., image.f.jpg)
		const lastDotIndex = baseUrl.lastIndexOf(".");
		if (lastDotIndex === -1) {
			urls.add(baseUrl);
			return;
		}

		const basePath = baseUrl.substring(0, lastDotIndex);
		const extension = baseUrl.substring(lastDotIndex);
		urls.add(`${basePath}.${size}${extension}`);
	}

	/**
	 * Normalize an image path to a full URL.
	 * Mirrors the logic from processImagePath in Functions.js
	 * @param {string} path - Image path
	 * @returns {string|null} Full URL or null
	 */
	#normalizeImageUrl(path) {
		if (!path) return null;

		// If it starts with /, prepend the base URL
		if (path[0] === "/") {
			path = REMOTE_BASE_URL + path;
		}

		// Skip if not a valid URL
		if (!path.startsWith("http")) return null;

		return path;
	}

	/**
	 * Cache a single image.
	 * @param {Cache} cache - Cache object
	 * @param {string} url - Image URL
	 * @returns {Promise<boolean>} Success
	 */
	async #cacheImage(cache, url) {
		try {
			const response = await fetch(url, {
				mode: "no-cors", // no-cors required for cross-origin CDN images without CORS headers
				signal: this.#abortController.signal,
			});

			// Opaque responses (type "opaque", status 0) work for <img> tags.
			// We can't verify success, but a network error would throw, so reaching
			// here means we got some response from the server.
			if (response.type === "opaque" || response.ok) {
				await cache.put(url, response);
				return true;
			}
			return false;
		} catch (error) {
			OfflineErrors.log("cacheImage", error, { url });
			return false;
		}
	}

	/**
	 * Fetch the first page of items with metadata.
	 * Items are sorted client-side by sequence when served from cache.
	 * @param {number|null} pageId - Catalog page ID to fetch items for (optional)
	 * @returns {Promise<Object>} First page result with items, metadata
	 */
	async #fetchPriorityFirstPage(pageId = null) {
		const catalogId = this.#catalogContext.id;
		if (!catalogId) {
			console.error("[OfflineSync] No catalog ID found in context:", this.#catalogContext);
			return null;
		}

		try {
			// Fetch meta (pages, count, sync_time)
			const meta = await this.#fetchMeta();

			// Fetch first page of items
			const endpoint = `Catalog/${catalogId}/items`;
			const params = {
				limit: 60, // First page of items for instant UI
			};

			// If a specific page is requested, filter by page_id
			if (pageId !== null) {
				params.page_id = pageId;
			}

			const response = await LiveApiClient.get(endpoint, {
				params,
				headers: {
					account: this.#catalogContext.account.code,
					"api-key": this.#apiKeyContext.apiKey,
				},
				signal: this.#abortController.signal,
			});
			const normalized = this.#normalizeItemsResponse(response, 60);

			// Combine normalized items and meta response
			return {
				...normalized,
				total: meta.count,
				pages: meta.pages,
				sync_time: meta.sync_time,
				fields: meta.fields,
				on_demand: meta.on_demand,
				meta_filters: meta.filters,
			};
		} catch (error) {
			console.warn("[OfflineSync] Priority first-page fetch failed, continuing with normal sync:", error);
			return null;
		}
	}

	/**
	 * Fetch a single page from the catalog items endpoint.
	 * Uses cursor-based pagination for consistent results.
	 * @param {Object} options
	 * @param {number|null} options.afterId - Cursor: item ID from previous page
	 * @param {string|null} options.since - ISO timestamp for delta sync
	 * @param {boolean} options.includeMeta - Whether to fetch meta (for first page in resume/delta)
	 * @param {number|null} options.pageId - Catalog page ID to filter by (for page-by-page sync)
	 */
	async #fetchPage({ afterId = null, since = null, includeMeta = false, pageId = null } = {}) {
		const catalogId = this.#catalogContext.id;
		if (!catalogId) {
			throw new Error("No catalog ID found");
		}

		// Fetch meta separately if needed (first page of resume/delta sync)
		let meta = null;
		if (includeMeta) {
			meta = await this.#fetchMeta();
		}

		const endpoint = `Catalog/${catalogId}/items`;
		const params = {
			limit: this.#PAGE_SIZE,
		};

		// Use cursor-based pagination if we have a cursor
		if (afterId !== null) {
			params.after_id = afterId;
		}

		if (since) {
			params.since = since;
		}

		// Filter by catalog page ID for page-by-page sync
		if (pageId !== null) {
			params.page_id = pageId;
		}

		const response = await LiveApiClient.get(endpoint, {
			params,
			headers: {
				account: this.#catalogContext.account.code,
				"api-key": this.#apiKeyContext.apiKey,
			},
			signal: this.#abortController.signal,
		});
		const normalized = this.#normalizeItemsResponse(response, this.#PAGE_SIZE);

		// Merge meta data if fetched
		if (meta) {
			return {
				...normalized,
				total: meta.count,
				pages: meta.pages,
				sync_time: meta.sync_time,
				fields: meta.fields,
				on_demand: meta.on_demand,
				meta_filters: meta.filters,
			};
		}

		return normalized;
	}

	/**
	 * Normalize an items API response into a consistent shape.
	 * Handles both flat array responses (current backend) and wrapped object responses.
	 * Reads pagination info from X-Next-Cursor response header.
	 * @param {object} response - Axios response object
	 * @param {number} limit - The limit used in the request (to detect has_more)
	 * @returns {{ items: Array, has_more: boolean, next_cursor: object|null }}
	 */
	#normalizeItemsResponse(response, limit) {
		const data = response.data;
		const items = Array.isArray(data) ? data : (data?.items || []);

		let nextCursor = null;
		let hasMore = false;

		// 1. Check X-Next-Cursor header (cursor-based pagination)
		//    Supports: JSON ({"after_id":"123"}), plain ID ("129421"), or URL (...?after_id=123)
		const nextCursorHeader = response.headers?.['x-next-cursor'];
		if (nextCursorHeader) {
			const raw = nextCursorHeader.trim();
			if (/^\d+$/.test(raw)) {
				// Plain numeric ID
				nextCursor = { after_id: Number(raw) };
				hasMore = true;
			} else {
				try {
					const parsed = JSON.parse(raw);
					if (parsed.after_id) {
						nextCursor = { after_id: Number(parsed.after_id) };
						hasMore = true;
					}
				} catch (e) {
					try {
						const url = new URL(raw, 'https://placeholder');
						const afterId = url.searchParams.get('after_id');
						if (afterId) {
							nextCursor = { after_id: Number(afterId) };
							hasMore = true;
						}
					} catch (e2) {
						console.warn("[OfflineSync] Failed to parse X-Next-Cursor header:", raw);
					}
				}
			}
		}

		// 2. Check X-Next header (offset-based, present on first page or non-cursor requests)
		//    Use as has_more indicator, but build cursor from last item ID
		if (!hasMore) {
			const nextHeader = response.headers?.['x-next'];
			if (nextHeader && items.length > 0) {
				hasMore = true;
				nextCursor = { after_id: items[items.length - 1].id };
			}
		}

		// Wrap flat API items into {attribs: {...}} format expected by
		// CachePrecacheItems, PrecacheIndex.ingestBatch, and FilterPrecachedItems.
		// Keeps top-level id, filters, group for consumers that read them there.
		const wrappedItems = items.map(item => {
			if (item.attribs) return item; // Already wrapped
			const { filters, group } = item;
			return {
				attribs: item,
				id: item.id,
				...(filters !== undefined && { filters }),
				...(group !== undefined && { group }),
			};
		});

		return { items: wrappedItems, has_more: hasMore, next_cursor: nextCursor };
	}

	/**
	 * Fetch metadata (pages, count) from the items/meta endpoint.
	 * @returns {Promise<Object>} Meta with { pages, count, sync_time }
	 */
	async #fetchMeta() {
		const catalogId = this.#catalogContext.id;
		if (!catalogId) {
			console.error("[OfflineSync] No catalog ID found in context:", this.#catalogContext);
			throw new Error("No catalog ID found");
		}

		const endpoint = `Catalog/${catalogId}/items/meta`;

		const response = await LiveApiClient.get(endpoint, {
			headers: {
				account: this.#catalogContext.account.code,
				"api-key": this.#apiKeyContext.apiKey,
			},
			signal: this.#abortController.signal,
		});

		return response.data;
	}

	/**
	 * Fetch assets from the items/assets endpoint.
	 * Returns all assets (images, videos, PDFs) associated with catalog items.
	 * @returns {Promise<Object>} { assets: Array, sync_time: string }
	 */
	async #fetchAssets() {
		const catalogId = this.#catalogContext.id;
		if (!catalogId) {
			console.error("[OfflineSync] No catalog ID found in context:", this.#catalogContext);
			throw new Error("No catalog ID found");
		}

		const endpoint = `Catalog/${catalogId}/items/assets`;

		const response = await LiveApiClient.get(endpoint, {
			headers: {
				account: this.#catalogContext.account.code,
				"api-key": this.#apiKeyContext.apiKey,
			},
			signal: this.#abortController.signal,
		});

		return response.data;
	}

	/**
	 * Check if a full sync is needed (no items cached).
	 * @returns {Promise<boolean>}
	 */
	static async NeedsFullSync(catalogId) {
		const store = OfflineStore.ForCatalog(catalogId);
		const count = await store.GetPrecacheItemCount();
		return count === 0;
	}

	/**
	 * Check if there's an incomplete sync that can be resumed.
	 * @param {string|number} catalogId
	 * @returns {Promise<{canResume: boolean, progress: object|null}>}
	 */
	static async GetResumableSync(catalogId) {
		const store = OfflineStore.ForCatalog(catalogId);
		const hasIncomplete = await store.HasIncompleteSyncProgress();
		if (!hasIncomplete) {
			return { canResume: false, progress: null };
		}
		const progress = await store.GetSyncProgress();
		return { canResume: true, progress };
	}

	/**
	 * Check if a delta sync is available (has previous sync time).
	 * @returns {Promise<boolean>}
	 */
	static async HasPreviousSync(catalogId) {
		const store = OfflineStore.ForCatalog(catalogId);
		const lastSync = await store.GetSyncMetadata("lastSyncTime");
		return !!lastSync;
	}

	/**
	 * Get sync status for a catalog.
	 * @returns {Promise<{ itemCount: number, lastSync: string|null, storageUsed: number, imageCache: object, assetCache: object }>}
	 */
	static async GetSyncStatus(catalogId) {
		const store = OfflineStore.ForCatalog(catalogId);
		const [itemCount, lastSync, storage, imageCache, assetCache] = await Promise.all([
			store.GetPrecacheItemCount(),
			store.GetSyncMetadata("lastSyncTime"),
			OfflineStore.GetStorageStats(),
			this.GetImageCacheStats(),
			this.GetAssetCacheStats(),
		]);

		return {
			itemCount,
			lastSync,
			storageUsed: storage.usage,
			storagePercent: storage.percent,
			imageCache,
			assetCache,
		};
	}

	/**
	 * Rebuild the term dictionary from existing cached items.
	 * Useful when the extraction logic changes without needing a full re-sync.
	 * @param {string|number} catalogId
	 */
	static async RebuildTermDictionary(catalogId) {
		const store = OfflineStore.ForCatalog(catalogId);

		const { items } = await store.GetPrecachedItems();
		if (items.length === 0) {
			console.warn("[OfflineSync] No cached items to build dictionary from");
			return 0;
		}

		const terms = new Set();
		const minTermLength = 3;

		for (const item of items) {
			const attribs = item.attribs || {};

			const searchableText = [
				attribs.name,
				attribs.title,
				attribs.description,
				attribs.handle,
				attribs.keywords,
				attribs.category,
				attribs.family,
				attribs.style,
				attribs.color_swatch,
			].filter(Boolean).join(" ");

			const words = searchableText
				.toLowerCase()
				.replace(/[^a-z0-9\s]/g, " ")
				.split(/\s+/)
				.filter(word => word.length >= minTermLength);

			words.forEach(word => terms.add(word));
		}

		await store.CacheTermDictionary(terms);
		return terms.size;
	}

	/**
	 * Clear all offline data for a catalog (deletes the entire IndexedDB database).
	 */
	static async ClearOfflineData(catalogId) {
		const store = OfflineStore.ForCatalog(catalogId);
		await store.ResetDatabase();
	}

	/**
	 * Clear the image cache.
	 */
	static async ClearImageCache() {
		try {
			await caches.delete(IMAGE_CACHE_NAME);
		} catch (error) {
			console.error("[OfflineSync] Failed to clear image cache:", error);
		}
	}

	/**
	 * Clear the asset cache (product page images, videos, PDFs).
	 */
	static async ClearAssetCache() {
		try {
			await caches.delete(ASSET_CACHE_NAME);
		} catch (error) {
			console.error("[OfflineSync] Failed to clear asset cache:", error);
		}
	}

	/**
	 * Get an image from cache, falling back to network.
	 * Use this for serving images in offline mode.
	 * @param {string} url - Image URL
	 * @returns {Promise<Response|null>}
	 */
	static async GetCachedImage(url) {
		try {
			const cache = await caches.open(IMAGE_CACHE_NAME);
			const cached = await cache.match(url);
			if (cached) return cached;

			// Not in cache, try network
			const response = await fetch(url);
			if (response.ok) {
				// Cache for future use
				cache.put(url, response.clone());
				return response;
			}
			return null;
		} catch (error) {
			return null;
		}
	}

	/**
	 * Get an asset from cache, falling back to network.
	 * Use this for serving product page assets in offline mode.
	 * @param {string} url - Asset URL
	 * @returns {Promise<Response|null>}
	 */
	static async GetCachedAsset(url) {
		try {
			const cache = await caches.open(ASSET_CACHE_NAME);
			const cached = await cache.match(url);
			if (cached) return cached;

			// Not in cache, try network
			const response = await fetch(url);
			if (response.ok) {
				// Cache for future use
				cache.put(url, response.clone());
				return response;
			}
			return null;
		} catch (error) {
			return null;
		}
	}

	/**
	 * Check if an image is cached.
	 * @param {string} url - Image URL
	 * @returns {Promise<boolean>}
	 */
	static async IsImageCached(url) {
		try {
			const cache = await caches.open(IMAGE_CACHE_NAME);
			const cached = await cache.match(url);
			return !!cached;
		} catch {
			return false;
		}
	}

	/**
	 * Get image cache statistics.
	 * Note: Size cannot be accurately measured for opaque (no-cors) responses,
	 * so we only return the count.
	 * @returns {Promise<{ count: number, size: number }>}
	 */
	static async GetImageCacheStats() {
		try {
			const cache = await caches.open(IMAGE_CACHE_NAME);
			const keys = await cache.keys();

			// Don't iterate through responses to calculate size - it's slow
			// and returns 0 for opaque responses anyway
			return {
				count: keys.length,
				size: 0,
				sizeFormatted: "N/A",
			};
		} catch {
			return { count: 0, size: 0, sizeFormatted: "N/A" };
		}
	}

	/**
	 * Get asset cache statistics.
	 * @returns {Promise<{ count: number, size: number }>}
	 */
	static async GetAssetCacheStats() {
		try {
			const cache = await caches.open(ASSET_CACHE_NAME);
			const keys = await cache.keys();

			return {
				count: keys.length,
				size: 0,
				sizeFormatted: "N/A",
			};
		} catch {
			return { count: 0, size: 0, sizeFormatted: "N/A" };
		}
	}

	/**
	 * Format bytes to human readable string.
	 */
	static #formatBytes(bytes) {
		if (bytes === 0) return "0 B";
		const k = 1024;
		const sizes = ["B", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
	}
}
